import { Component, ViewChild } from '@angular/core';
import { AuthService } from 'src/app/services/auth.service';
import {
  AuthorizeNetSavedPaymentMethod,
  PaymentsService,
} from 'src/app/services/payments.service';
import { Tenant, TenantsService } from 'src/app/services/tenants.service';
import { RemovePaymentMethodModalComponent } from '../saved-payment-methods/remove-payment-method-modal/remove-payment-method-modal.component';
import { AddPaymentMethodModalComponent } from '../saved-payment-methods/add-payment-method-modal/add-payment-method-modal.component';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-tenant-settings',
  templateUrl: './tenant-settings.component.html',
  styleUrl: './tenant-settings.component.scss',
})
export class TenantSettingsComponent {
  @ViewChild('addPaymentMethodModal')
  addPaymentMethodModal!: AddPaymentMethodModalComponent;
  @ViewChild('removePaymentMethodModal')
  removePaymentMethodModal!: RemovePaymentMethodModalComponent;

  loadingTenant = false;
  errorFetchingTenant = '';
  tenant?: Tenant;

  loadingPaymentMethods = false;
  errorFetchingPaymentMethods = '';
  savedPaymentMethods?: AuthorizeNetSavedPaymentMethod[];
  savedPaymentError = '';

  showAutopayWarning = false;
  autopayError = '';

  methodToRemove?: AuthorizeNetSavedPaymentMethod;
  settingDefaultMethod = false;

  constructor(
    private authService: AuthService,
    private tenantsService: TenantsService,
    private paymentsService: PaymentsService,
    private toastService: ToastService
  ) {}

  ngOnInit() {
    this.getTenantDetails();
    this.getSavedPaymentMethods();
  }

  get canManagePaymentMethods() {
    return (
      this.authService.currentUser?.permissions.find(
        (p) =>
          p.role.name === 'super_admin' ||
          p.role.name === 'internal_admin' ||
          p.role.name === 'admin' ||
          p.role.name === 'owner'
      ) !== undefined
    );
  }

  getTenantDetails() {
    this.loadingTenant = true;
    this.tenantsService.getCurrentTenantDetails().subscribe({
      next: (tenant) => {
        this.loadingTenant = false;
        this.tenant = tenant.tenant;
      },
      error: (err) => {
        console.error(err);
        this.loadingTenant = false;
        this.errorFetchingTenant = "Error fetching tenant's details";
      },
    });
  }

  getSavedPaymentMethods() {
    this.loadingPaymentMethods = true;
    this.paymentsService.getCurrentUserSavedPaymentMethods().subscribe({
      next: (res) => {
        this.loadingPaymentMethods = false;
        this.savedPaymentMethods = res.payment_profiles;
      },
      error: (err) => {
        console.error(err);
        this.loadingPaymentMethods = false;
        this.errorFetchingPaymentMethods =
          'Error fetching saved payment methods';
      },
    });
  }

  setDefaultPaymentMethod(paymentMethod: AuthorizeNetSavedPaymentMethod) {
    if (!this.savedPaymentMethods) {
      console.error(new Error('No saved payment methods set'));
      this.savedPaymentError = 'No saved payment methods found';
      return;
    }

    this.settingDefaultMethod = true;
    console.log('setting default payment method: ', paymentMethod);
    this.paymentsService.setDefaultPaymentMethod(paymentMethod).subscribe({
      next: (res) => {
        this.savedPaymentError = '';
        console.log('Payment method set as default:', res);
        this.settingDefaultMethod = false;
        this.savedPaymentMethods!.forEach((p) => {
          p.is_default = p.id === paymentMethod.id;
        });
      },
      error: (err) => {
        console.error(err);
        this.settingDefaultMethod = false;
        this.savedPaymentError =
          'There was an error setting your saved payment method.';
      },
    });
  }

  setApiDefaultPaymentMethod(paymentMethod: AuthorizeNetSavedPaymentMethod) {
    if (!this.savedPaymentMethods) {
      console.error(new Error('No saved payment methods set'));
      this.savedPaymentError = 'No saved payment methods found';
      return;
    }

    this.settingDefaultMethod = true;
    this.paymentsService.setApiDefaultPaymentMethod(paymentMethod).subscribe({
      next: (res) => {
        this.savedPaymentError = '';
        console.log('Payment method set as API default:', res);
        this.settingDefaultMethod = false;
        this.savedPaymentMethods!.forEach((p) => {
          p.is_api_default = p.id === paymentMethod.id;
        });
      },
      error: (err) => {
        console.error(err);
        this.settingDefaultMethod = false;
        this.savedPaymentError =
          'There was an error setting your API default payment method.';
      },
    });
  }

  handlePaymentMethodCreation(paymentMethod: AuthorizeNetSavedPaymentMethod) {
    if (!this.savedPaymentMethods) {
      this.errorFetchingPaymentMethods =
        'Error handling payment method addition';
      return;
    }

    this.addPaymentMethodModal.hideAddMethodModal();
    this.toastService.addToast('success', 'Payment method added successfully');

    this.savedPaymentMethods.push(paymentMethod);
    // if returned payment method is default, update all others
    if (paymentMethod.is_default) {
      this.savedPaymentMethods.forEach((p) => {
        p.is_default = p.id === paymentMethod.id;
      });
    }
    if (paymentMethod.is_api_default) {
      this.savedPaymentMethods.forEach((p) => {
        p.is_api_default = p.id === paymentMethod.id;
      });
    }
  }

  confirmDeleteSavedMethod(paymentMethod: AuthorizeNetSavedPaymentMethod) {
    this.methodToRemove = paymentMethod;
    this.removePaymentMethodModal.showRemoveMethodModal();
    // this.removeMethodModal.nativeElement.showModal();
  }

  savedMethodDeleted(paymentMethod: AuthorizeNetSavedPaymentMethod) {
    console.log('saved method deleted: ', paymentMethod);
    this.savedPaymentMethods = this.savedPaymentMethods!.filter(
      (p) => p.id !== paymentMethod.id
    );
  }

  handleAutopayToggle(ev: Event) {
    if (!this.tenant) {
      console.error(new Error('No tenant set'));
      return;
    }

    if (this.tenant?.autopay_enabled) {
      // autopay enabled, show warning before disabling
      ev.preventDefault();
      this.showAutopayWarning = true;
      return;
    }

    this.tenant.autopay_enabled = true;
    this.showAutopayWarning = false;
    this.saveAutopaySetting(this.tenant.autopay_enabled);
  }

  disableAutopay() {
    if (!this.tenant) {
      console.error(new Error('No tenant set'));
      return;
    }

    this.tenant.autopay_enabled = false;
    this.showAutopayWarning = false;
    this.saveAutopaySetting(this.tenant.autopay_enabled);
  }

  saveAutopaySetting(autopay: boolean) {
    console.log('saving autopay setting: ', autopay);

    if (!this.tenant) {
      this.autopayError = 'Tenant not found';
      return;
    }

    this.tenantsService.updateTenantAutopay(this.tenant.id, autopay).subscribe({
      next: () => {
        this.autopayError = '';
      },
      error: (err) => {
        // revert to previous value since update failed
        this.tenant!.autopay_enabled = !autopay;
        if (
          err.error.message === 'no payment methods found for tenant' ||
          err.error.message === 'no default payment method found for tenant'
        ) {
          this.autopayError =
            'Must have a deafult payment method to enable autopay';
        } else {
          this.autopayError = err.error.message;
        }
      },
    });
  }
}
